import { useEffect, useRef, useState } from "react";
import { animate } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const REGIONS = [
  { id: "nam", label: "North America", factor: 0.42 },
  { id: "emea", label: "EMEA", factor: 0.28 },
  { id: "apac", label: "APAC", factor: 0.22 },
  { id: "latam", label: "LATAM", factor: 0.08 },
  { id: "global", label: "Global", factor: 1 },
];
const SENIORITY = [
  { id: "csuite", label: "C-Suite", factor: 0.07 },
  { id: "vp", label: "VP Level", factor: 0.16 },
  { id: "director", label: "Director+", factor: 0.34 },
  { id: "all", label: "All Decision-Makers", factor: 0.55 },
];
const VERTICALS = [
  { id: "saas", label: "Enterprise SaaS", base: 4200000 },
  { id: "cyber", label: "Cybersecurity", base: 1900000 },
  { id: "cloud", label: "Cloud & DevOps", base: 2600000 },
  { id: "fintech", label: "FinTech", base: 1500000 },
  { id: "ai", label: "AI & Analytics", base: 1700000 },
  { id: "hrtech", label: "HR Tech", base: 1100000 },
];

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] tracking-[0.22em] text-slate-400">{label}</span>
    <span className="relative mt-2 block">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:border-cobaltlight/50"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id} className="bg-navy text-white">
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </span>
  </label>
);

export const ReachCalculator = () => {
  const [region, setRegion] = useState("nam");
  const [seniority, setSeniority] = useState("director");
  const [vertical, setVertical] = useState("saas");
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);

  const r = REGIONS.find((x) => x.id === region);
  const s = SENIORITY.find((x) => x.id === seniority);
  const v = VERTICALS.find((x) => x.id === vertical);
  const estimate = Math.round((v.base * r.factor * s.factor) / 1000) * 1000;

  useEffect(() => {
    const controls = animate(prev.current, estimate, {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (x) => setDisplay(Math.round(x)),
    });
    prev.current = estimate;
    return () => controls.stop();
  }, [estimate]);

  const format = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(2)}M` : `${Math.round(n / 1000)}K`);

  return (
    <div className="grid gap-10 lg:grid-cols-2" data-testid="reach-calculator">
      <div className="space-y-6">
        <Select label="TARGET REGION" value={region} onChange={setRegion} options={REGIONS} testId="calc-region-select" />
        <Select label="SENIORITY LEVEL" value={seniority} onChange={setSeniority} options={SENIORITY} testId="calc-seniority-select" />
        <Select label="INDUSTRY VERTICAL" value={vertical} onChange={setVertical} options={VERTICALS} testId="calc-industry-select" />
      </div>
      <div className="flex flex-col justify-between rounded-2xl border border-cobalt/30 bg-gradient-to-br from-eblue/60 to-navy p-8">
        <div>
          <p className="font-mono2 text-[10px] tracking-[0.24em] text-cobaltlight">ESTIMATED IN-MARKET DECISION-MAKERS</p>
          <p className="mt-4 font-jakarta text-6xl font-extrabold tracking-tight text-white tabular-nums sm:text-7xl" data-testid="calc-result">
            ~{format(display)}
          </p>
          <p className="mt-2 text-sm text-slate-300">
            verified contacts matching {s.label} · {v.label} · {r.label}
          </p>
        </div>
        <Link
          to="/contact-us"
          data-testid="calc-feasibility-btn"
          className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-flame px-6 py-3.5 text-sm font-bold text-navy transition-all duration-300 hover:scale-[1.02] hover:bg-[#ffb43a] active:scale-95"
        >
          Get Campaign Feasibility & Cost-Per-Lead
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};
